import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Plus, Pencil, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/useConfirm";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PeriodBar } from "@/components/PeriodBar";
import { dayLabel, money, rangeFor, weekDays, ymd } from "@/lib/periods";

const emptyForm = (user) => ({
  site: user?.primary_site || "SITE_1",
  date: ymd(new Date()),
  supplier: "",
  order_ref: "",
  items_ordered: "",
  total_cost: "",
  delivery_date: "",
  ordered_by: user?.name || "",
  notes: "",
});

function OrderDialog({ open, onClose, initial, sites, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial); }, [open, initial]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const editing = Boolean(form.id);

  const save = async () => {
    if (!form.supplier.trim() || !form.date) {
      toast.error("Supplier and date are required");
      return;
    }
    setBusy(true);
    const payload = {
      site: form.site, date: form.date, supplier: form.supplier.trim(),
      order_ref: form.order_ref, items_ordered: form.items_ordered,
      total_cost: Number(form.total_cost) || 0, delivery_date: form.delivery_date,
      ordered_by: form.ordered_by, notes: form.notes,
    };
    try {
      if (editing) await api.patch(`/ordering/entries/${form.id}`, payload);
      else await api.post("/ordering/entries", payload);
      toast.success(editing ? "Order updated" : "Order recorded");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ordering-entry-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit order" : "Record supplier order"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Site</Label>
            <Select value={form.site} onValueChange={set("site")}>
              <SelectTrigger data-testid="ordering-form-site"><SelectValue /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Order date</Label>
            <Input type="date" value={form.date} onChange={(e) => set("date")(e.target.value)} data-testid="ordering-form-date" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Supplier</Label>
            <Input placeholder="e.g. Fresh Farm Produce Ltd" value={form.supplier} onChange={(e) => set("supplier")(e.target.value)} data-testid="ordering-form-supplier" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Order reference</Label>
            <Input placeholder="PO-1042 / standing order" value={form.order_ref} onChange={(e) => set("order_ref")(e.target.value)} data-testid="ordering-form-ref" />
          </div>
          <div className="col-span-2 flex flex-col gap-1">
            <Label className="text-xs">Items ordered</Label>
            <Textarea rows={2} placeholder="Mixed salad leaves, tomatoes 10kg…" value={form.items_ordered} onChange={(e) => set("items_ordered")(e.target.value)} data-testid="ordering-form-items" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Total cost ($)</Label>
            <Input type="number" min="0" step="0.01" value={form.total_cost} onChange={(e) => set("total_cost")(e.target.value)} data-testid="ordering-form-cost" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Expected delivery</Label>
            <Input type="date" value={form.delivery_date} onChange={(e) => set("delivery_date")(e.target.value)} data-testid="ordering-form-delivery" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Ordered by</Label>
            <Input value={form.ordered_by} onChange={(e) => set("ordered_by")(e.target.value)} data-testid="ordering-form-ordered-by" />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes} onChange={(e) => set("notes")(e.target.value)} data-testid="ordering-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#1A2744] text-white" data-testid="ordering-form-save">
            {editing ? "Save changes" : "Add order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OrderingSheet() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const isAdmin = ["ADMIN", "SUPERADMIN"].includes(user?.role);
  const [mode, setMode] = useState("WEEK");
  const [anchor, setAnchor] = useState(new Date());
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [site, setSite] = useState("ALL");
  const [sites, setSites] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dialog, setDialog] = useState(null);

  const range = useMemo(
    () => (mode === "CUSTOM" ? (custom.start && custom.end ? custom : null) : rangeFor(mode, anchor)),
    [mode, anchor, custom]
  );

  useEffect(() => {
    api.get("/config").then(({ data }) => setSites(data.sites || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!range) return;
    const params = { start: range.start, end: range.end };
    if (site !== "ALL") params.site = site;
    try {
      const [a, b] = await Promise.all([
        api.get("/ordering/entries", { params }),
        api.get("/ordering/summary", { params }),
      ]);
      setEntries(a.data.entries || []);
      setSummary(b.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [range?.start, range?.end, site]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const remove = async (row) => {
    const ok = await confirm({ title: "Delete order?", description: `Remove order from "${row.supplier}" (${money(row.total_cost)}).`, confirmText: "Delete" });
    if (!ok) return;
    try { await api.delete(`/ordering/entries/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const byDay = useMemo(() => {
    const map = {};
    entries.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [entries]);
  const days = mode === "WEEK" && range ? weekDays(range) : Object.keys(byDay).sort();
  const siteLabel = (c) => sites.find((s) => s.code === c)?.label || c;

  return (
    <div className="space-y-6" data-testid="ordering-sheet-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] flex items-center gap-3">
            <ShoppingCart className="h-8 w-8 text-[#C9A84C]" /> Ordering Sheet
          </h1>
          <p className="text-gray-500 mt-1">Supplier order log — Mon to Sun, live spend totals.</p>
        </div>
        <Button onClick={() => setDialog(emptyForm(user))} className="bg-[#1A2744] text-white" data-testid="ordering-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Record order
        </Button>
      </header>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-wrap">
        <PeriodBar mode={mode} setMode={setMode} anchor={anchor} setAnchor={setAnchor} custom={custom} setCustom={setCustom} range={range} idPrefix="ordering-period" />
        <div className="lg:ml-auto">
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="w-[170px] h-8" data-testid="ordering-site-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All sites</SelectItem>
              {sites.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-[#C9A84C]/50">
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">Total ordering spend</div>
            <div className="text-3xl font-bold text-[#1A2744] mt-1" data-testid="ordering-total-cost">{money(summary?.total_cost)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">Orders</div>
            <div className="text-3xl font-bold text-[#1A2744] mt-1" data-testid="ordering-entry-count">{summary?.entries || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="text-[11px] uppercase tracking-widest text-gray-500">Suppliers</div>
            <div className="text-3xl font-bold text-[#1A2744] mt-1" data-testid="ordering-supplier-count">{summary?.by_supplier?.length || 0}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 space-y-4">
          {days.length === 0 && (
            <Card><CardContent className="p-10 text-center text-gray-400 text-sm">No orders recorded in this period.</CardContent></Card>
          )}
          {days.map((d) => {
            const rows = byDay[d] || [];
            const dayTotal = rows.reduce((s, r) => s + (r.total_cost || 0), 0);
            return (
              <Card key={d} data-testid={`ordering-day-${d}`}>
                <CardContent className="p-0">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-[#1A2744]/[0.04] border-b border-gray-100">
                    <span className="text-sm font-semibold text-[#1A2744]">{dayLabel(d)}</span>
                    <span className="text-sm font-mono font-semibold text-[#1A2744]" data-testid={`ordering-day-total-${d}`}>{money(dayTotal)}</span>
                  </div>
                  {rows.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-gray-400">No orders</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Supplier</TableHead>
                            <TableHead>Ref</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead>Site</TableHead>
                            <TableHead>Delivery</TableHead>
                            <TableHead>Ordered by</TableHead>
                            <TableHead className="text-right">Total $</TableHead>
                            {isAdmin && <TableHead />}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((r) => (
                            <TableRow key={r.id} data-testid={`ordering-row-${r.id}`}>
                              <TableCell className="font-medium text-sm">{r.supplier}</TableCell>
                              <TableCell className="text-xs text-gray-500">{r.order_ref || "—"}</TableCell>
                              <TableCell className="text-xs text-gray-600 max-w-[220px] truncate" title={r.items_ordered}>{r.items_ordered || "—"}</TableCell>
                              <TableCell className="text-xs text-gray-500">{siteLabel(r.site)}</TableCell>
                              <TableCell className="text-xs text-gray-500">{r.delivery_date || "—"}</TableCell>
                              <TableCell className="text-xs text-gray-600">{r.ordered_by || "—"}</TableCell>
                              <TableCell className="text-right text-sm font-mono font-semibold">{money(r.total_cost)}</TableCell>
                              {isAdmin && (
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDialog({ ...r, total_cost: String(r.total_cost) })} data-testid={`ordering-edit-${r.id}`}>
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(r)} data-testid={`ordering-delete-${r.id}`}>
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card data-testid="ordering-by-supplier">
          <CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-3">Spend by supplier</div>
            {(summary?.by_supplier || []).length === 0 ? (
              <div className="text-xs text-gray-400">No spend in this period.</div>
            ) : (
              <div className="space-y-2">
                {summary.by_supplier.map((s) => (
                  <div key={s.supplier} className="flex items-center justify-between text-sm border-b border-gray-100 pb-1.5 last:border-0">
                    <span className="text-[#1A2744] truncate mr-2">{s.supplier} <span className="text-[10px] text-gray-400">×{s.count}</span></span>
                    <span className="font-mono font-semibold">{money(s.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {dialog && <OrderDialog open onClose={() => setDialog(null)} initial={dialog} sites={sites} onSaved={load} />}
    </div>
  );
}
